import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {
  animate,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import {
  take,
  takeUntil,
  Subject,
  lastValueFrom,
  firstValueFrom,
  skip,
} from 'rxjs';

import { Router, UrlTree } from '@angular/router';

import { Store } from '@ngrx/store';
import {
  ExtendedKeycloakUser,
  PurchaseType,
  StoreDropdowns,
  UserInformation,
} from 'src/app/shared/models/app-models';
import { BackendApiService } from 'src/app/core/services/backend-api';
import { PageEvent } from '@angular/material/paginator';
import { clearTaggingStore } from 'src/app/core/store/TaggedVendors.action';
import { clearAttachmentStore } from 'src/app/core/store/attachment.action';
import { clearStore } from 'src/app/core/store/form.actions';
import { clearDeletedItemVendorStore } from 'src/app/core/store/DeletedItemVendors.action';
import { MyWorkListData, MyWorklist } from 'src/app/shared/models/database-models/MyWorklist.model';
import { pageName } from 'src/app/shared/services/page.service';
import {Clipboard} from '@angular/cdk/clipboard';

export interface RoutingToCE {
  RoutingToCE: boolean
}
@Component({
  selector: 'app-my-worklist-listing',
  templateUrl: './my-worklist-listing.component.html',
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0' })),
      state('expanded', style({ height: '*' })),
      transition(
        'expanded <=> collapsed',
        animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')
      ),
    ]),
  ],
  styleUrls: ['./my-worklist-listing.component.scss'],
})
export class MyWorklistComponent implements OnInit, OnDestroy, OnChanges {
  @ViewChild(MatSort) sort!: MatSort;
  @Input() tabStatus: string = '';
  @Input() index!: number;
  @Input() selectedIndex!: number;
  @Input() initialData: MyWorklist[] = [];
  @Input() badgeCount: number = 0;
  isLoadingData: boolean = true;
  documents: MyWorklist[] = [];
  dataSource: MatTableDataSource<MyWorklist> =
    new MatTableDataSource<MyWorklist>(this.documents);
  wlColumnsToDisplay = [
    'DocumentNo',
    'ModifiedOn',
    'PurposeOfPurchase',
    'Workflow',
    'PurchaseMode',
    'RequestingOfficer',
    'SubmittedOn',
  ];
  columnsToDisplayWithExpand = [...this.wlColumnsToDisplay];
  expandedElement!: MyWorklist | null;
  destroy$: Subject<boolean> = new Subject<boolean>();
  userList: ExtendedKeycloakUser[] = [];
  user!: UserInformation;
  pageSize: number = 20;
  emptyMessage: string = '';
  pageSizeOptions: number[] = [20, 40, 60];
  currentPage: number = 0;
  totalCount: number = 0;
  sortSettingMap: any = {};
  currentSort: string | null = null;
  constructor(
    private router: Router,
    private store: Store<{ dp: StoreDropdowns; user: UserInformation }>,
    private backendService: BackendApiService,
    private clipboard: Clipboard
  ) {
    this.store
      .select('dp')
      .pipe(takeUntil(this.destroy$))
      .subscribe((dp: StoreDropdowns) => {
        this.userList = dp.UserList as ExtendedKeycloakUser[];
      });
    this.store
      .select('user')
      .pipe(takeUntil(this.destroy$))
      .subscribe(async (user: UserInformation) => {
        this.user = user;
      });
    this.initSort()
  }
  async ngOnInit(): Promise<void> { 
    this.store.dispatch(clearStore());
    this.store.dispatch(clearTaggingStore());
    this.store.dispatch(clearDeletedItemVendorStore())
    this.store.dispatch(clearAttachmentStore());
  }
  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    if (
      changes['selectedIndex'] &&
      changes['selectedIndex'].currentValue == this.index
    ) {
      try {
        // ensure that user is not null due to update sequence
        if (this.user.userName === '') {
          this.user = await firstValueFrom(
            this.store.select('user').pipe(skip(1))
          );
        }
        if (this.isLoadingData) {
          // await this.loadDataBasedOnPageAndLimit();
          await this.loadDataBasedOnPageAndLimit(this.initialData);
          this.isLoadingData = false;
        }
      } catch (e) {
        console.log('Error retrieving MyWorklist');
        this.dataSource.data = [];
        this.isLoadingData = false;
      }
    }
  }

  async loadDataBasedOnPageAndLimit(initialData?: MyWorklist[]) {
    if (!initialData) {
      let data: MyWorkListData = await lastValueFrom(
        this.backendService.getDocumentsForAO(
          this.user.userName,
          this.tabStatus,
          this.pageSize.toString(),
          (this.currentPage + 1).toString(),
          this.currentSort,
          this.currentSort ? this.sortSettingMap[this.currentSort] : null
        )
      );
      this.documents = data.documents.map((docs: MyWorklist) => {
        return {
          ...docs,
          RequestingOfficer:
            this.userList.find((user) => user.id == docs.RequestingOfficer)
              ?.last_name ?? '',
          RFQSummaryRequestingOfficer: docs.IsRFQSummaryEnable
            ? this.userList.find((user: ExtendedKeycloakUser) => user.id === docs.RFQSummaryRequestingOfficer)?.last_name ?? 'unknown'
            : null
        };
      });
      this.dataSource.data = this.documents;
      this.totalCount = data.totalCount;
    } else {
      this.documents = initialData.map((docs: MyWorklist) => {
        return {
          ...docs,
          RequestingOfficer:
            this.userList.find((user) => user.id == docs.RequestingOfficer)
              ?.last_name ?? '',
          RFQSummaryRequestingOfficer: docs.IsRFQSummaryEnable
            ? this.userList.find((user: ExtendedKeycloakUser) => user.id === docs.RFQSummaryRequestingOfficer)?.last_name ?? 'unknown'
            : null
        };
      });
      this.dataSource.data = this.documents;
      this.totalCount = this.badgeCount;
    }

    setTimeout(() => {
      this.isLoadingData = false;
      if (this.totalCount == 0) this.getEmptyMessage(this.index);
    }, 100);
  }

  async onPageChange(event: PageEvent) {
    // console.log('onPageChange', event);
    this.currentPage = event.pageIndex;
    this.pageSize = event.pageSize;
    await this.loadDataBasedOnPageAndLimit();
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  ngAfterViewInit(): void {
    this.dataSource.sort = this.sort;
  }

  formatHeader(camelCaseHeader: string): string {
    const words = camelCaseHeader
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, function (str) {
        return str.toUpperCase();
      });
    if (words.trim().toLowerCase() === 'document no') {
      return words + '.';
    }
    if (words.trim().toLowerCase() === 'modified on') {
      return 'Last Modified Date';
    }
    if (words.trim().toLowerCase() === 'workflow') {
      return 'Required Workflow Action';
    }
    return words;
  }
  async shouldRouteToCE(element: MyWorklist): Promise<any> {
    const data = await lastValueFrom(
      this.backendService.RoutingtoCE(
        element.DocumentNo,
        this.user.userName)
    );
    return data.RoutingtoCE
  }
  async navigateToAOWorkList(element: MyWorklist, action: string | null = null) {
    sessionStorage.setItem('fromPage',pageName.My_worklist);
    if (element.PurchaseMode === PurchaseType.TENDER && element.Workflow == 'Approve tender documents') {
      const path = `worklist/${element.DocumentNo}/${element.dag_run_id}`;
      if(action == "tab") {
        window.open(path, "_blank");
      } else if(action == "copy-link") {
        this.clipboard.copy(path);
      } else {
        this.router.navigateByUrl(path);
      }
      return;
    }
    if (await this.shouldRouteToCE(element)) {
      let mode = 'tender';
      if (element.PurchaseMode === PurchaseType.DC) {
        mode = 'dc'
      }
      sessionStorage.setItem('currentTab', '0');
      const path = `/purchase/${mode}/${element.DocumentNo}`;
      if(action == "tab") {
        window.open(path, "_blank");
      } else if(action == "copy-link") {
        this.clipboard.copy(path);
      } else {
        this.router.navigateByUrl(path);
      }
      return;
    }
    if (Object.values(WorkFlowsToRONavigate).includes(element.Workflow as WorkFlowsToRONavigate)) {
      sessionStorage.setItem('currentTab', '0');
      const path = this.getRouteToRoView(element.PurchaseMode as PurchaseType, element.DocumentNo);
      if(action == "tab") {
        window.open(path, "_blank");
      } else if (action == "copy-link") {
        this.clipboard.copy(path);
      } else {
        this.router.navigateByUrl(path);
      }
      return;
    }
    else {
      const path = `worklist/${element.DocumentNo}/${element.dag_run_id}`;
      if(action == "tab") { 
        window.open(path, "_blank");
      } else if (action == "copy-link") {
        this.clipboard.copy(path);
      } else {
        this.router.navigateByUrl(path);
      }
      return;
    }
  }
  navigateToRFQSummary(element: MyWorklist) {
    sessionStorage.setItem('fromPage',pageName.My_worklist);
    const url = '/rfqSummary/' + `${element.DocumentNo}`;
    window.open(url, '_blank');
  }
  getRouteToRoView(purchaseMode: PurchaseType, DocumentNo: string) {
    let url = ''
    switch (purchaseMode) {
      case PurchaseType.RFQ:
        url = '/purchase/rfq/' + `${DocumentNo}`;
        break;
      case PurchaseType.TENDER:
        url = '/purchase/tender/' + `${DocumentNo}`;
        break;
      case PurchaseType.CORPORATE_CARD:
        url = '/purchase/corporatecard/' + `${DocumentNo}`;
        break;
      case PurchaseType.PM:
        url = '/purchase/permissiblecoveringpurchaseorder/' + `${DocumentNo}`;
        break;
      case PurchaseType.PCPO:
        url = '/purchase/permissiblecoveringpurchaseorder/' + `${DocumentNo}`;
        break;
      case PurchaseType.DC:
        url = '/purchase/dc/' + `${DocumentNo}`;
        break;
      case PurchaseType.ONLINE:
        url = '/purchase/corporatecard/' + `${DocumentNo}`;
        break;
    }
    return url
  }
  
  onContextMenuClick(element: MyWorklist, action: string) {
    if(action === "open") {
      this.navigateToAOWorkList(element);
    } else if (action === "tab") {
      this.navigateToAOWorkList(element, action);
    } else if (action === "copy-link") {
      let link = this.navigateToAOWorkList(element, action);
    } else if (action === "copy-documentnumber") {
      this.clipboard.copy(element.DocumentNo);
    }
  }

  getEmptyMessage(tabIndex: number) {
    switch (tabIndex) {
      case 0:
        this.emptyMessage = 'You have no pending document items.';
        break;
      case 1:
        this.emptyMessage = 'You have no completed document items.';
        break;
      default:
        this.emptyMessage = 'You have no document items.';
    }
  }

  initSort() {
    for (const column of this.wlColumnsToDisplay) {
      this.sortSettingMap[column] = true;
    }
  }


  resetSort(columnToSort: string) {
    for (const column of this.wlColumnsToDisplay) {
      if (column != columnToSort) {
        this.sortSettingMap[column] = true;
      }
    }
  }

  sortByColumn(column: string) {
    if (this.currentSort != column) {
      this.resetSort(column);
      this.currentSort = column;
    } else {
      this.sortSettingMap[column] = !this.sortSettingMap[column];
    }
    // api call to update order
    this.loadDataBasedOnPageAndLimit();
  }
}

enum WorkFlowsToRONavigate {
  Source_of_vendors = 'Source for vendors',
  Quotation_upload = 'Quotation upload',
  Raise_po = 'Raise Purchase Order',
  Generate_RFQ = 'Awaiting RFQ Generation',
  RFQ_generated = 'RFQ Generated'
}